import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { CAPABILITIES } from '../../lib/site';

export default function Capabilities() {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || paused) return;
        const t = setInterval(() => setIndex((i) => (i + 1) % CAPABILITIES.length), 5500);
        return () => clearInterval(t);
    }, [paused]);

    const cap = CAPABILITIES[index];
    const Icon = cap.icon;

    return (
        <section
            id="capabilities"
            data-testid="home-capabilities"
            className="relative py-20 lg:py-28"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-5">
                    <span className="dt-section-eyebrow">CORE CAPABILITIES</span>
                    <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                        Deep engineering capability across the
                        <br /> <span className="dt-gradient-text">complete digital stack</span>.
                    </h2>
                    <p className="mt-4 text-muted-foreground max-w-md leading-relaxed">
                        From software architecture and advanced programming to AI, cloud, data, cybersecurity, and automation, we build the connected technology foundations that modern organizations depend on.
                    </p>

                    <ul className="mt-8 space-y-1">
                        {CAPABILITIES.map((c, i) => (
                            <li key={c.key}>
                                <button
                                    onClick={() => setIndex(i)}
                                    data-testid={`capability-tab-${c.key}`}
                                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors ${
                                        i === index
                                            ? 'border-primary/50 bg-primary/5 text-foreground'
                                            : 'border-white/5 hover:border-white/15 hover:bg-white/5 text-foreground/75'
                                    }`}
                                >
                                    <c.icon className={`h-4 w-4 ${i === index ? 'text-primary' : 'text-foreground/55'}`} />
                                    <span className="font-medium text-sm">{c.title}</span>
                                    <span className="ml-auto font-mono text-[10px] tracking-widest text-muted-foreground">
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-6 flex items-center gap-2">
                        <button
                            data-testid="capabilities-carousel-prev"
                            onClick={() => setIndex((i) => (i - 1 + CAPABILITIES.length) % CAPABILITIES.length)}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-white/10 hover:border-primary/40 hover:bg-white/5"
                            aria-label="Previous"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                            data-testid="capabilities-carousel-next"
                            onClick={() => setIndex((i) => (i + 1) % CAPABILITIES.length)}
                            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-white/10 hover:border-primary/40 hover:bg-white/5"
                            aria-label="Next"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <span className="ml-2 text-xs font-mono text-muted-foreground">
                            {String(index + 1).padStart(2, '0')} / {String(CAPABILITIES.length).padStart(2, '0')}
                        </span>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <div className="dt-neon-border" data-testid="capabilities-carousel">
                        <div className="dt-neon-inner p-6 sm:p-8 lg:p-10 min-h-[420px] relative overflow-hidden">
                            <div className="absolute -top-16 -right-12 h-72 w-72 rounded-full bg-[radial-gradient(closest-side,rgba(0,229,255,0.18),transparent)] pointer-events-none" />
                            <div className="absolute -bottom-20 -left-16 h-80 w-80 rounded-full bg-[radial-gradient(closest-side,rgba(72,255,175,0.12),transparent)] pointer-events-none" />
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={cap.key}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.4, ease: 'easeOut' }}
                                    className="relative"
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <span className="font-mono text-[11px] tracking-[0.22em] text-primary">{cap.eyebrow}</span>
                                    </div>
                                    <h3 className="font-display mt-5 text-2xl sm:text-3xl font-semibold tracking-tight">
                                        {cap.title}
                                    </h3>
                                    <p className="mt-3 text-foreground/85 max-w-xl leading-relaxed">
                                        {cap.body}
                                    </p>
                                    <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {cap.bullets.map((b) => (
                                            <div
                                                key={b}
                                                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-foreground/85"
                                            >
                                                {b}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-8 inline-flex items-center gap-1.5 text-sm text-primary hover:text-accent cursor-pointer">
                                        See how we ship this <ArrowRight className="h-4 w-4" />
                                    </div>
                                </motion.div>
                            </AnimatePresence>

                            {/* Progress dots */}
                            <div className="absolute bottom-4 left-6 right-6 flex items-center gap-2">
                                {CAPABILITIES.map((c, i) => (
                                    <span
                                        key={c.key}
                                        className={`h-0.5 flex-1 rounded-full transition-colors ${
                                            i === index ? 'bg-primary' : 'bg-white/15'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
